'use client';
import React, { useState, useEffect } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Home, Settings, LogOut, Thermometer, CloudRain, Droplets, Wifi, WifiOff, Phone, Camera, CheckCircle, AlertTriangle, Users, CheckSquare, Square, Send, X } from 'lucide-react';
import { auth } from '../services/firebase';
import { Suspense, lazy } from 'react';
import RecommendationCard from './RecommendationCard';
import AnalyticsPanel from './AnalyticsPanel'; // Added import
import EmergencyOverlay from './EmergencyOverlay';
import ConfigPanel from './ConfigPanel';
import AdminPanel from './AdminPanel';
import LinkingPanel from './LinkingPanel';
import SensorCard from './SensorCard';
import CitizenReportModal from './CitizenReportModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useWebSocket } from '../hooks/useWebSocket';
import { api } from '../lib/api';

const MapCard = lazy(() => import('./MapCard'));
// Helper para fallback simple
const LoadingMap = () => <div className="h-48 w-full bg-slate-800 animate-pulse rounded-lg flex items-center justify-center">Cargando Mapa...</div>;

export default function Dashboard({ user }) {
    const [data, setData] = useState({
        waterLevel: 0,
        temperature: 0,
        humidity: 0,
        deviceId: 'device-001'
    });
    const [config, setConfig] = useState({
        sector: 'gazcue',
        gps: null
    });
    const [showAdmin, setShowAdmin] = useState(false); // Admin visibility state
    const [emergency, setEmergency] = useState(null);
    const [analysis, setAnalysis] = useState(null); // Added state
    const [showCitizenReport, setShowCitizenReport] = useState(false); // Citizen Report Modal

    // Contact Selection State
    const [showContactSelector, setShowContactSelector] = useState(false);
    const [contactsList, setContactsList] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState(new Set());

    const { connected, sensorData, emergencyAlert, subscribeToDevice } = useWebSocket();

    // Subscribe to device on mount
    useEffect(() => {
        if (connected && data.deviceId) {
            subscribeToDevice(data.deviceId);
        }
    }, [connected, data.deviceId, subscribeToDevice]);

    // Update local data when WebSocket receives sensor updates
    useEffect(() => {
        if (sensorData) {
            setData(prev => ({
                ...prev,
                ...sensorData
            }));
            if (sensorData.analysis) {
                setAnalysis(sensorData.analysis);
            }
        }
    }, [sensorData]);

    // Handle emergency alerts from WebSocket
    useEffect(() => {
        if (emergencyAlert) {
            setEmergency(emergencyAlert);
        }
    }, [emergencyAlert]);

    // Fetch initial sensor data
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await api.sensors.getLatest(data.deviceId);
                if (response.data) {
                    setData(prev => ({ ...prev, ...response.data }));
                }
            } catch (error) {
                console.error('Error fetching sensor data:', error);
                // Fallback to mock data if backend is not available
                setData(prev => ({
                    ...prev,
                    waterLevel: 5,
                    temperature: 28.5,
                    humidity: 65
                }));
            }
        };
        fetchInitialData();
    }, [data.deviceId]);

    const calculateRisk = () => {
        if (data.waterLevel > 20) return 'high';
        if (config.sector === 'los-prados') return 'high';
        if (config.sector === 'villa-mella') return 'medium';
        return 'low';
    };

    const riskLevel = calculateRisk();

    const handleEmergencyTrigger = async (level, msg) => {
        const alert = {
            level,
            message: msg,
            sector: config.sector
        };

        setEmergency(alert);

        // Send to backend
        try {
            await api.alerts.create(alert);
        } catch (error) {
            console.error('Error creating alert:', error);
        }

    };

    const toggleContact = (index) => {
        const next = new Set(selectedContacts);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        setSelectedContacts(next);
    };

    const handleConfirmSend = async () => {
        const selected = contactsList.filter((_, i) => selectedContacts.has(i));

        if (selected.length === 0) {
            alert('⚠️ Debes seleccionar al menos un contacto.');
            return;
        }

        setShowContactSelector(false); // Close modal

        const loc = config.gps || 'Ubicación Desconocida';
        const mapLink = `https://maps.google.com/?q=${loc.replace(/\s/g, '')}`;
        const msg = `🆘 AYUDA URGENTE: Emergencia de inundación.\n📍 Mi ubicación actual: ${mapLink}\nPor favor envía ayuda.`;

        try {
            // Optimistic UI update
            alert(`🚀 Enviando alerta a ${selected.length} contactos seleccionados...`);

            await api.alerts.create({
                level: 'critical',
                message: msg,
                sector: config.sector,
                contacts: selected
            });

        } catch (e) {
            console.error(e);
            // Fallback to manual WhatsApp
            const encodedMsg = encodeURIComponent(msg);
            window.open(`https://api.whatsapp.com/send?text=${encodedMsg}`, '_blank');
        }
    };

    // Handle citizen report submission
    const handleCitizenReportSubmit = async (reportData) => {
        try {
            // Send to backend API
            await api.alerts.create({
                type: 'citizen_report',
                level: reportData.waterLevelEstimate === 'alto' ? 'high' : reportData.waterLevelEstimate === 'medio' ? 'medium' : 'low',
                message: reportData.description,
                location: reportData.location,
                photo: reportData.photo,
                timestamp: reportData.timestamp,
                sector: config.sector
            });
            console.log('Citizen report submitted:', reportData);
        } catch (error) {
            console.error('Error submitting citizen report:', error);
            // Store locally if backend fails
            const storedReports = JSON.parse(localStorage.getItem('pending_reports') || '[]');
            storedReports.push({ ...reportData, sector: config.sector });
            localStorage.setItem('pending_reports', JSON.stringify(storedReports));
        }
    };

    return (
        <div className="layout-container">
            {showAdmin ? (
                <AdminPanel onExit={() => setShowAdmin(false)} />
            ) : (
                <>
                    {/* Header */}
                    <header className="app-header glass-effect">
                        <div className="header-left">
                            <div className="logo-icon">🌊</div>
                            <div>
                                <h2>Floppy Floods</h2>
                                <span className={`status-badge ${riskLevel}`}>
                                    Riesgo: {riskLevel.toUpperCase()}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {/* Botón de Emergencia 911 */}
                            <a href="tel:911" className="btn-icon" style={{ background: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', textDecoration: 'none', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)', marginRight: '0.5rem' }} title="Llamar al 911">
                                <Phone size={18} color="white" />
                            </a>

                            {/* WebSocket Status Indicator */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {connected ? (
                                    <>
                                        <Wifi size={16} color="#10b981" />
                                        <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Live</span>
                                    </>
                                ) : (
                                    <>
                                        <WifiOff size={16} color="#ef4444" />
                                        <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>Offline</span>
                                    </>
                                )}
                            </div>
                            <button onClick={() => auth.signOut()} className="btn-icon" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <LogOut size={20} color="#94a3b8" />
                            </button>
                        </div>
                    </header>

                    {/* Radix Tabs for Navigation */}
                    <Tabs.Root className="TabsRoot" defaultValue="home">

                        <Tabs.Content className="TabsContent" value="home">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="app-content dashboard-grid"
                            >
                                {/* Safety Check Widget */}
                                <div className="safety-check-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
                                    <button className="btn glass-effect" style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #10b981', color: '#10b981', height: 'auto', padding: '10px' }} onClick={() => alert('✅ Estado actualizado: ESTOY A SALVO. Se ha notificado a tus contactos.')}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <CheckCircle size={24} style={{ marginBottom: 5 }} />
                                            <span style={{ fontWeight: 'bold' }}>Estoy a salvo</span>
                                        </div>
                                    </button>
                                    <button className="btn glass-effect" style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', color: '#ef4444', height: 'auto', padding: '10px' }}
                                        onClick={() => {
                                            const contacts = JSON.parse(localStorage.getItem('emergency_contacts') || '[]');
                                            if (contacts.length === 0) {
                                                alert('⚠️ No tienes contactos guardados. Agrega contactos en Configuración.');
                                                return;
                                            }
                                            setContactsList(contacts);
                                            // Default: Select ALL
                                            setSelectedContacts(new Set(contacts.map((_, i) => i)));
                                            setShowContactSelector(true);
                                        }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <AlertTriangle size={24} style={{ marginBottom: 5 }} />
                                            <span style={{ fontWeight: 'bold' }}>Necesito Ayuda</span>
                                        </div>
                                    </button>
                                </div>

                                <Suspense fallback={<LoadingMap />}>
                                    <MapCard sector={config.sector} riskLevel={riskLevel} gps={config.gps} />
                                </Suspense>

                                <AnalyticsPanel analysis={analysis} />

                                <AnalyticsPanel analysis={analysis} />
                                <RecommendationCard riskLevel={riskLevel} />

                                <div className="sensors-grid">
                                    <SensorCard
                                        title="Temperatura"
                                        value={`${data.temperature?.toFixed(1) || '0.0'}°C`}
                                        icon={Thermometer}
                                        color="#fbbf24"
                                    />
                                    <SensorCard
                                        title="Humedad"
                                        value={`${data.humidity?.toFixed(0) || '0'}%`}
                                        icon={CloudRain}
                                        color="#60a5fa"
                                    />
                                    <SensorCard
                                        title="Nivel Agua"
                                        value={`${data.waterLevel?.toFixed(1) || '0.0'} cm`}
                                        icon={Droplets}
                                        color="#3b82f6"
                                        progress={data.waterLevel || 0}
                                        max={30}
                                    />
                                </div>
                            </motion.div>
                        </Tabs.Content>

                        <Tabs.Content className="TabsContent" value="config">
                            <div className="app-content">
                                <ConfigPanel
                                    config={config}
                                    setConfig={setConfig}
                                    triggerEmergency={handleEmergencyTrigger}
                                    onAdminLogin={() => setShowAdmin(true)}
                                />
                            </div>
                        </Tabs.Content>

                        <Tabs.Content className="TabsContent" value="link">
                            <div className="app-content">
                                <LinkingPanel user={user} />
                            </div>
                        </Tabs.Content>

                        {/* Bottom Nav using Tabs.List */}
                        <Tabs.List className="glass-effect TabsList glow-effect" aria-label="Navigation">
                            <Tabs.Trigger className="TabsTrigger" value="home">
                                <Home size={24} />
                                <span>Inicio</span>
                            </Tabs.Trigger>

                            <Tabs.Trigger className="TabsTrigger" value="link">
                                <Users size={24} />
                                <span>Comunidad</span>
                            </Tabs.Trigger>

                            <div className="nav-fab" onClick={() => setShowCitizenReport(true)}>
                                📷
                            </div>

                            <Tabs.Trigger className="TabsTrigger" value="config">
                                <Settings size={24} />
                                <span>Config</span>
                            </Tabs.Trigger>
                        </Tabs.List>
                    </Tabs.Root>

                    {/* Citizen Report FAB */}
                    <button
                        onClick={() => setShowCitizenReport(true)}
                        className="fab-report"
                        style={{
                            position: 'fixed', bottom: 90, right: 20,
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            border: 'none', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            zIndex: 100, cursor: 'pointer'
                        }}
                        title="Reportar inundación con foto"
                    >
                        <Camera size={24} />
                    </button>

                    {/* Emergency Overlay */}
                    <AnimatePresence>
                        {emergency && (
                            <EmergencyOverlay
                                emergency={emergency}
                                onDismiss={() => setEmergency(null)}
                            />
                        )}
                    </AnimatePresence>

                    {/* Contact Selection Modal */}
                    <AnimatePresence>
                        {showContactSelector && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                style={{
                                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                    background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(5px)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
                                }}
                            >
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="card glass-effect"
                                    style={{ width: '90%', maxWidth: '400px', padding: '20px', border: '1px solid #334155' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                        <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                                            <Users className="text-blue-400" size={24} /> Seleccionar Destinatarios
                                        </h3>
                                        <button onClick={() => setShowContactSelector(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                                            <X size={24} />
                                        </button>
                                    </div>

                                    <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                                        {contactsList.map((c, i) => {
                                            const isSelected = selectedContacts.has(i);
                                            return (
                                                <div
                                                    key={i}
                                                    onClick={() => toggleContact(i)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 15, padding: 12, borderRadius: 10,
                                                        background: isSelected ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255,255,255,0.05)',
                                                        border: isSelected ? '1px solid #3b82f6' : '1px solid transparent',
                                                        cursor: 'pointer', transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <div style={{ color: isSelected ? '#3b82f6' : '#64748b' }}>
                                                        {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold' }}>{c.name}</div>
                                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{c.phone}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button
                                            onClick={() => setShowContactSelector(false)}
                                            className="btn"
                                            style={{ flex: 1, background: '#334155', color: '#cbd5e1' }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleConfirmSend}
                                            className="btn"
                                            style={{
                                                flex: 2, background: '#ef4444', color: 'white',
                                                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                                                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
                                            }}
                                        >
                                            <Send size={18} /> Enviar Alerta ({selectedContacts.size})
                                        </button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Citizen Report Modal */}
                    <CitizenReportModal
                        isOpen={showCitizenReport}
                        onClose={() => setShowCitizenReport(false)}
                        onSubmit={handleCitizenReportSubmit}
                        initialGps={config.gps}
                    />

                    <PrivacyModal />
                </>
            )}
        </div>
    );
}

function PrivacyModal() {
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        const hasConcent = localStorage.getItem('privacy_consent');
        if (hasConcent) setAccepted(true);
    }, []);

    if (accepted) return null;

    return (
        <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            background: 'rgba(15, 23, 42, 0.95)', borderTop: '1px solid #334155',
            padding: '20px', zIndex: 1000, backdropFilter: 'blur(10px)',
            display: 'flex', flexDirection: 'column', gap: 15, alignItems: 'center'
        }}>
            <div style={{ maxWidth: 600, width: '100%' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#fff' }}>🔒 Privacidad y Confianza</h3>
                <p style={{ fontSize: '0.9rem', color: '#cbd5e1', lineHeight: '1.4' }}>
                    Esta aplicación utiliza tu ubicación <strong>solo cuando activas una emergencia</strong> o solicitas ver tu posición en el mapa.
                    Los datos son anónimos y no se comparten con terceros no autorizados.
                    <br /><br />
                    Al continuar, aceptas la recolección mínima de datos para fines de seguridad civil.
                </p>
                <button
                    onClick={() => {
                        localStorage.setItem('privacy_consent', 'true');
                        setAccepted(true);
                    }}
                    className="btn btn-primary"
                    style={{ marginTop: 15, width: '100%' }}
                >
                    Entendido y Aceptar
                </button>
            </div>
        </div>
    );
}
